import { Page, View, Text, Image } from '@react-pdf/renderer';
import { StyleSheet } from '@react-pdf/renderer';
import { LaudoHeader, DadosPedido, DadosImovel, ResumoAvaliacao, DetalhamentoValores, Footer } from '../../../types';
import { AVMPageFooter } from '../AVM/components';

const s = StyleSheet.create({
  page: { width: 595, height: 842, backgroundColor: '#FFFFFF', flexDirection: 'column', fontFamily: 'Helvetica' },
  content: { flex: 1, padding: 16, flexDirection: 'column', gap: 16 },
  headerBar: { paddingBottom: 8, borderBottom: '1 solid #D6DBE7', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F6F9', gap: 8, paddingRight: 8 },
  accent: { width: 3, height: 28, backgroundColor: '#0090FF' },
  sectionTitle: { color: '#000000', fontSize: 9, fontWeight: 'bold' },
  dataRow: { flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7' },
  label: { color: '#000000', fontSize: 7.5, fontWeight: 'normal' },
  value: { color: '#000000', fontSize: 9, fontWeight: 'bold' },
  darkBox: { paddingHorizontal: 8, paddingVertical: 6, backgroundColor: '#001F3F', flexDirection: 'column', gap: 2 },
  darkLabel: { color: '#FFFFFF', fontSize: 7.5, fontWeight: 'normal' },
  darkValue: { color: '#FFFFFF', fontSize: 12, fontWeight: 'bold' },
});

function F({ label, val, width, flex, paddingRight }: { label: string; val: string | number; width?: number; flex?: number; paddingRight?: number }) {
  const safe = val === '' || val === undefined || val === null ? '-' : val;
  const cs: any = { flexDirection: 'column', gap: 2, overflow: 'hidden' };
  if (width) cs.width = width; else cs.flex = flex ?? 1;
  if (paddingRight !== undefined) cs.paddingRight = paddingRight;
  return <View style={cs}><Text style={s.label}>{label}</Text><Text style={s.value}>{safe}</Text></View>;
}

function SH({ title }: { title: string }) {
  return <View style={s.sectionHeader}><View style={s.accent} /><Text style={s.sectionTitle}>{title}</Text></View>;
}

interface IdentificacaoProps {
  header: LaudoHeader;
  dadosPedido: DadosPedido;
  dadosImovel: DadosImovel;
  resumoAvaliacao: ResumoAvaliacao;
  detalhamentoValores: DetalhamentoValores;
  fotoFachadaUrl?: string;
  footer: Footer;
  logoBase64?: string;
}

export function Identificacao({
  header, dadosPedido, dadosImovel, resumoAvaliacao, detalhamentoValores, fotoFachadaUrl, footer, logoBase64,
}: IdentificacaoProps) {
  const isEvolutivo = resumoAvaliacao.metodologiaAplicada?.toLowerCase().includes('evolutivo');

  return (
    <Page size="A4" style={s.page}>
      <View style={s.content}>
        {/* HEADER BAR */}
        <View style={{ flexDirection: 'column', gap: 8 }}>
          <View style={s.headerBar}>
            {logoBase64 ? (
              <Image src={logoBase64} style={{ width: 64, height: 24, objectFit: 'contain' }} />
            ) : (
              <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#101010' }}>bari.</Text>
            )}
            <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <Text style={{ textAlign: 'right', color: '#000000', fontSize: 10, fontWeight: 'bold' }}>LAUDO DE AVALIAÇÃO</Text>
              <Text style={{ textAlign: 'right', color: '#000000', fontSize: 9 }}>{header.idLaudo}  ·  {header.formatoLaudo}</Text>
            </View>
          </View>
        </View>

        {/* SECTIONS gap-6=24 */}
        <View style={{ flexDirection: 'column', gap: 24 }}>

          {/* DADOS DO PEDIDO */}
          <View style={{ flexDirection: 'column' }}>
            <SH title="DADOS DO PEDIDO" />
            <View style={s.dataRow}>
              <F label="Solicitante" val={dadosPedido.solicitanteNome} width={176} />
              <F label="N° da Proposta" val={dadosPedido.propostaNumero} width={112} />
              <F label="Data de solicitação" val={dadosPedido.dataSolicitacao} />
              <F label="Tipo de laudo" val="Com Vistoria" />
            </View>
            <View style={s.dataRow}>
              <F label="Empresa Responsável" val={dadosPedido.empresaResponsavelNome} width={176} />
              <F label="CREA" val={dadosPedido.avaliadorCrea} width={112} />
              <F label="Avaliador Responsável" val={dadosPedido.avaliadorResponsavelNome} />
              <View style={{ flex: 1 }} />
            </View>
            <View style={s.dataRow}>
              <F label="Proponente" val={dadosPedido.proponenteNome} width={176} />
              <F label="CPF/CNPJ" val={dadosPedido.proponenteDocumento} width={112} />
              <View style={{ flex: 1 }} />
              <View style={{ flex: 1 }} />
            </View>
          </View>

          {/* DADOS DO IMÓVEL */}
          <View style={{ flexDirection: 'column' }}>
            <SH title="DADOS DO IMÓVEL" />
            <View style={s.dataRow}>
              <F label="Endereço" val={dadosImovel.enderecoRua || '-'} width={176} />
              <F label="Número" val={dadosImovel.enderecoNumero || 'S/N'} width={112} />
              <F label="Complemento" val={dadosImovel.enderecoComplemento || '-'} />
              <View style={{ flex: 1 }} />
            </View>
            <View style={s.dataRow}>
              <F label="Bairro" val={dadosImovel.enderecoBairro} width={176} />
              <F label="UF" val={dadosImovel.enderecoUf} width={112} />
              <F label="Município" val={dadosImovel.enderecoMunicipio} />
              <F label="CEP" val={dadosImovel.enderecoCep} />
            </View>
            <View style={s.dataRow}>
              <F label="IPTU" val={dadosImovel.iptuNumero} width={176} />
              <F label="N° Reg. Imóveis" val={dadosImovel.matriculaRegistroImoveis} width={112} paddingRight={2} />
              <F label="Tipo do imóvel" val={dadosImovel.tipoImovel} />
              <F label="Matrícula" val={dadosImovel.matriculaNumero} />
            </View>
          </View>

          {/* RESUMO DA AVALIAÇÃO */}
          <View style={{ flexDirection: 'column' }}>
            <SH title="RESUMO DA AVALIAÇÃO" />
            <View style={{ flexDirection: 'row' }}>
              {/* Image: w-72=288 self-stretch */}
              <View style={{ width: 288, backgroundColor: '#E7E7E7', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                {fotoFachadaUrl ? (
                  <Image src={fotoFachadaUrl} style={{ width: 288, height: 220, objectFit: 'cover' }} />
                ) : (
                  <Text style={{ color: '#000000', fontSize: 9 }}>[ Imagem do Imóvel ]</Text>
                )}
              </View>
              {/* Fields: flex-1 */}
              <View style={{ flex: 1, flexDirection: 'column' }}>
                {/* Metodologia */}
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={s.label}>Metodologia</Text>
                  <Text style={s.value}>{resumoAvaliacao.metodologiaAplicada}</Text>
                </View>
                {/* Grau de Fundamentação */}
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={s.label}>Grau de Fundamentação</Text>
                  <Text style={s.value}>{resumoAvaliacao.grauFundamentacao}</Text>
                </View>
                {/* Grau de Precisão */}
                <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                  <Text style={s.label}>Grau de Precisão</Text>
                  <Text style={s.value}>{resumoAvaliacao.grauPrecisao}</Text>
                </View>

                {isEvolutivo ? (
                  <>
                    {/* Split: Área Terreno | Área Construída */}
                    <View style={{ flexDirection: 'row' }}>
                      <View style={{ flex: 1, paddingHorizontal: 8, paddingVertical: 6, borderRight: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                        <Text style={s.label}>Área Terreno</Text>
                        <Text style={s.value}>{detalhamentoValores.areaTerreno}</Text>
                      </View>
                      <View style={{ flex: 1, paddingHorizontal: 8, paddingVertical: 6, flexDirection: 'column', gap: 2 }}>
                        <Text style={s.label}>Área Construída</Text>
                        <Text style={s.value}>{detalhamentoValores.areaConstruida}</Text>
                      </View>
                    </View>
                    {/* Split: Valor uni. terreno | Valor uni. construída */}
                    <View style={{ flexDirection: 'row' }}>
                      <View style={{ flex: 1, paddingHorizontal: 8, paddingVertical: 6, borderRight: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                        <Text style={s.label}>Valor uni. terreno (R$/m²)</Text>
                        <Text style={s.value}>{detalhamentoValores.valorUnitarioTerreno}</Text>
                      </View>
                      <View style={{ flex: 1, paddingHorizontal: 8, paddingVertical: 6, flexDirection: 'column', gap: 2 }}>
                        <Text style={s.label}>Valor uni. área construída (R$/m²)</Text>
                        <Text style={s.value}>{detalhamentoValores.valorUnitarioConstruida}</Text>
                      </View>
                    </View>
                    {/* Split: Valor total terreno | Valor total construída */}
                    <View style={{ flexDirection: 'row' }}>
                      <View style={{ flex: 1, paddingHorizontal: 8, paddingVertical: 6, borderRight: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                        <Text style={s.label}>Valor total terreno</Text>
                        <Text style={s.value}>{detalhamentoValores.valorParcialTerreno}</Text>
                      </View>
                      <View style={{ flex: 1, paddingHorizontal: 8, paddingVertical: 6, flexDirection: 'column', gap: 2 }}>
                        <Text style={s.label}>Valor total área construída</Text>
                        <Text style={s.value}>{detalhamentoValores.valorParcialConstruida}</Text>
                      </View>
                    </View>
                  </>
                ) : (
                  <>
                    {/* Comparativo direto: Área Privativa, Valor unitário, Somente Área averbada */}
                    <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                      <Text style={s.label}>Área Privativa</Text>
                      <Text style={s.value}>{resumoAvaliacao.areaPrivativaTotal}</Text>
                    </View>
                    <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                      <Text style={s.label}>Valor unitário (R$/m²)</Text>
                      <Text style={s.value}>{resumoAvaliacao.valorUnitarioFinalM2}</Text>
                    </View>
                    <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', flexDirection: 'column', gap: 2 }}>
                      <Text style={s.label}>Somente Área averbada</Text>
                      <Text style={s.value}>{resumoAvaliacao.valorSomenteAreaAverbada || resumoAvaliacao.valorFinalGarantia}</Text>
                    </View>
                  </>
                )}

                {/* Dark box: Valor para efeito de garantia */}
                <View style={s.darkBox}>
                  <Text style={s.darkLabel}>Valor para efeito de garantia</Text>
                  <Text style={s.darkValue}>{resumoAvaliacao.valorFinalGarantia}</Text>
                </View>
              </View>
            </View>
          </View>

        </View>
      </View>

      <AVMPageFooter footer={footer} />
    </Page>
  );
}
